import React, { Suspense, useState, useEffect, useRef } from 'react'
import TabbedContent from '@/components/tabbed-content/TabbedContent'
import constants from '@/constants'
import { getPositionLabels } from '@/features/market/utils/positionLabels'

// Lazy load OrderBook component
const OrderBook = React.lazy(() => import('@/features/market/order-book'))

const OutcomeAccordion = ({
  outcome,
  question,
  parentQuestion,
  activeCurrency,
  isOrderBookAvailable,
  isMultiOutcomeMarket,
  longOrderbook,
  shortOrderbook,
  userOrders,
  displayMode,
  onPriceClick,
  handleOrderBookPositionChange,
  position,
  setPosition,
  activeTabIndex,
  setActiveTabIndex,
  shouldShowPositionButtons = false,
}) => {
  const buildAccordionTabs = () => {
    const isActive = outcome?.status === 'active'
    const isResolved = outcome?.status === 'resolved'
    const hasResolutionDescription = !!outcome?.resolution_description
    const hasDescription = !!outcome?.description
    const contents = []
    const positionLabels = getPositionLabels(outcome, question || parentQuestion)

    // Add Orderbook tabs (only if active and orderbook is available)
    // Note: mono markets (1 physical outcome) still need an orderbook inside the accordion.
    if (isActive && isOrderBookAvailable) {
      const renderOrderBookTab = (positionType, orderbookData) => {
        return (
          <div>
            <Suspense
              fallback={
                <div className="p-4">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-surface-3 rounded w-32"></div>
                    <div className="h-4 bg-surface-3 rounded w-20"></div>
                  </div>
                </div>
              }>
              <OrderBook
                question={question || parentQuestion}
                currency={activeCurrency}
                orders={orderbookData}
                isLoading={orderbookData?.status === constants.LOADING}
                spread={orderbookData?.spread}
                outcome={outcome}
                positionType={positionType}
                setOutcome={() => {}}
                setPositionType={handleOrderBookPositionChange}
                onChangeOutcome={() => {}}
                open={true}
                setOpen={() => {}}
                onInfoClick={() => {}}
                wagerSelected={null}
                userOrders={userOrders}
                onOpenOrderbook={() => {}}
                onScrollToOrderbook={() => {}}
                onSelectOrderbookPrice={() => {}}
                onPriceClick={onPriceClick}
                isMarketPage={false}
                showHeader={false}
                selectorType="tabs"
                showTabs={false}
              />
            </Suspense>
          </div>
        )
      }

      if (shouldShowPositionButtons) {
        // Show separate tabs for Long and Short when shouldShowPositionButtons is true
        contents.push({
          label: `Trade ${positionLabels.longLabel}`,
          render: () => renderOrderBookTab(constants.position.LONG, longOrderbook),
        })

        contents.push({
          label: `Trade ${positionLabels.shortLabel}`,
          render: () => renderOrderBookTab(constants.position.SHORT, shortOrderbook),
        })
      } else {
        // Show single tab when shouldShowPositionButtons is false
        // For virtual outcomes, outcome.title is already the position label (e.g., "Yes" or "No")
        // For actual outcomes, outcome.title is the outcome name
        const tabLabel = outcome?.title ? `Trade ${outcome.title}` : 'Trade'
        
        // For virtual outcomes (mono markets), use the orderbook that matches the virtual position
        // Virtual outcomes have _virtualPosition set to either LONG or SHORT
        const isVirtualOutcome = outcome?._virtualPosition !== undefined
        const orderbookToUse = isVirtualOutcome
          ? (outcome._virtualPosition === constants.position.LONG ? longOrderbook : shortOrderbook)
          : (longOrderbook || shortOrderbook)
        const positionToUse = isVirtualOutcome
          ? outcome._virtualPosition
          : position
        
        contents.push({
          label: tabLabel,
          render: () => renderOrderBookTab(positionToUse, orderbookToUse),
        })
      }
    }

    // Add Rules tab if description exists
    if (hasDescription) {
      contents.push({
        label: 'Rules',
        render: () => (
          <div >
            <div className="prose prose-sm max-w-none text-base leading-[150%] pt-0 pb-4">
              <div dangerouslySetInnerHTML={{ __html: outcome.description }} />
            </div>
          </div>
        ),
      })
    }

    // Add Resolution tab if resolved and has resolution_description
    if (isResolved && hasResolutionDescription) {
      contents.push({
        label: 'Resolution',
        render: () => (
          <div >
            <div className="prose prose-sm max-w-none text-base leading-[150%] pt-0 pb-4">
              <div dangerouslySetInnerHTML={{ __html: outcome.resolution_description }} />
            </div>
          </div>
        ),
      })
    }

    return contents
  }

  const tabs = buildAccordionTabs()
  if (tabs.length === 0) {
    return null
  }

  // Track previous position to avoid unnecessary updates
  const prevPositionRef = useRef(position)
  const prevOutcomeIdRef = useRef(outcome?.id)
  // Track if position change came from tab change (to avoid loops)
  const isPositionChangeFromTabRef = useRef(false)

  // Reset position ref when outcome changes
  useEffect(() => {
    if (prevOutcomeIdRef.current !== outcome?.id) {
      prevOutcomeIdRef.current = outcome?.id
      prevPositionRef.current = position
    }
  }, [outcome?.id, position])

  // Sync tab index when position changes (from PlaceBet or other external sources)
  useEffect(() => {
    // Only sync if shouldShowPositionButtons is true (we have separate Long/Short tabs)
    if (!shouldShowPositionButtons) return

    // Skip if position change came from tab change (to avoid loops)
    if (isPositionChangeFromTabRef.current) {
      isPositionChangeFromTabRef.current = false
      prevPositionRef.current = position
      return
    }

    // Only update if position actually changed
    if (prevPositionRef.current === position) return

    const isActive = outcome?.status === 'active'
    const isOrderBookAvailable = (question || parentQuestion)?.order_book_enabled
    // Only sync for active markets with orderbook
    if (isActive && isOrderBookAvailable) {
      // First two tabs are orderbook tabs (Long = 0, Short = 1)
      if (position === constants.position.LONG && activeTabIndex !== 0) {
        setActiveTabIndex(0)
      } else if (position === constants.position.SHORT && activeTabIndex !== 1) {
        setActiveTabIndex(1)
      }
    }

    prevPositionRef.current = position
  }, [position, shouldShowPositionButtons, outcome?.status, question, parentQuestion, activeTabIndex, setActiveTabIndex])

  // If only one tab, render it directly without TabbedContent
  if (tabs.length === 1) {
    const SingleTabContent = tabs[0].render
    return (
      <div
        className={displayMode === 'segment' ? 'border-t border-solid border-Neutral-3' : ''}>
        <div className="p-0 md:px-4 md:pt-4">
          {/* <h4 className="font-bold text-xl mb-4 text-Neutral-5">{tabs[0].label}</h4> */}
          <SingleTabContent />
        </div>
      </div>
    )
  }

  const handleTabChange = index => { 
    setActiveTabIndex(index)
    // Update position when switching to orderbook tabs
    const isActive = outcome?.status === 'active'
    const isOrderBookAvailable = (question || parentQuestion)?.order_book_enabled
    if (isActive && isOrderBookAvailable) {
      if (shouldShowPositionButtons) {
        // Mark that position change is coming from tab change to avoid loop
        isPositionChangeFromTabRef.current = true
        // First two tabs are orderbook tabs (long and short)
        if (index === 0) {
          setPosition(constants.position.LONG)
          handleOrderBookPositionChange(constants.position.LONG)
        } else if (index === 1) {
          setPosition(constants.position.SHORT)
          handleOrderBookPositionChange(constants.position.SHORT)
        }
      }
      // When shouldShowPositionButtons is false, position is already set and doesn't need to change
    }
  }

  return (
    <div
      className={`pt-4 ${displayMode === 'segment' ? 'px-4 pb-2 border-t border-solid border-Neutral-3' : ''}`}>
      <TabbedContent contents={tabs} defaultIndex={0} activeIndex={activeTabIndex} onContentChange={handleTabChange} />
    </div>
  )
}

export default OutcomeAccordion

